/**
 * Gestion du formulaire de contact conditionnel
 * - Niveau 1 : pro_perso (radio) → filtre les options de type_entreprise
 * - Niveau 2 : type_demande → affiche/masque les blocs data-form-show
 * - Niveau 3 : type_entreprise → cas administration, uploads carte/attestation, blocs structure
 * - Niveau 4 : taille_entreprise + type_entreprise → bloc structure (raison sociale, dirigeant, effectif, infos légales)
 * - Niveau 5 : pays → état (US/CA), TVA (UE), SIRET (FR), KBIS (FR + pro structure)
 * - Gère la visibilité du libellé "Saisie obligatoire *" et les attributs required
 *
 * @version 1.0.0 - 22/04/2026
 */

import { parseQuantity } from './contact-quantity'

// Types d'entreprise considérés comme "structure d'entreprise" (déclenchent dirigeant, raison sociale, effectif, infos légales)
// Cette liste doit rester en miroir de la validation serveur
const TYPES_ENTREPRISE_AVEC_STRUCTURE = ['esn', 'corpo', 'isv', 'ecole', 'independant', /*'cabinet',*/ 'revendeur', 'association', 'administration']
const TAILLES_AVEC_STRUCTURE = ['micro_entreprise', 'petite_entreprise', 'moyenne_entreprise', 'grande_entreprise', 'administration']

// Types d'entreprise nécessitant un KBIS si pays = FR (exclut independant/cabinet/administration/ecole/association :
// pas de KBIS au sens strict). Doit rester en miroir de la validation serveur
const TYPES_ENTREPRISE_KBIS = ['esn', 'corpo', 'isv', 'revendeur']

// Types d'entreprise nécessitant TVA / SIRET (= tous les types pro, en miroir de la validation serveur)
const TYPES_ENTREPRISE_REQUIRED_TVA = ['esn', 'corpo', 'isv', 'independant', /*'cabinet',*/ 'revendeur', 'administration', 'ecole', 'association']
const TYPES_ENTREPRISE_REQUIRED_SIRET = ['esn', 'corpo', 'isv', 'independant', /*'cabinet',*/ 'revendeur', 'administration', 'ecole', 'association']

// Pays pour lesquels les champs sont conditionnels (aligné sur config/contact.php)
const PAYS_TVA_REQUIRED = ['BE','BG','CZ','DK','DE','EE','IE','ES','FR','GR','HR','IT','CY','LV','LT','LU','HU','MT','NL','AT','PT','PL','RO','SI','SK','FI','SE']
const PAYS_SIRET_REQUIRED = ['FR']
const PAYS_KBIS_REQUIRED = ['FR']
const PAYS_ETAT_REQUIRED = ['US', 'CA']

// Seuils d'effectif par taille d'entreprise (en miroir des libellés Blade contact.blade.php options du select taille_entreprise)
// Pas de miroir backend : validation cohérence uniquement côté front
const TAILLE_EFFECTIF_RANGES: Record<string, { min: number; max: number | null }> = {
    micro_entreprise:   { min: 1,   max: 9   },
    petite_entreprise:  { min: 1,   max: 49  },
    moyenne_entreprise: { min: 1,   max: 249 },
    grande_entreprise:  { min: 250, max: null },
}

// SIRET : algorithme INSEE = Luhn modulo 10 par défaut, exception La Poste (préfixe 356) sur somme directe % 5
// Faille Luhn connue : 14 zéros passe (somme = 0, 0 % 10 = 0) → check anti-zéros explicite
const isLaPosteSiret = (s: string): boolean => {
    if (!s.startsWith('356')) return false
    let sum = 0
    for (const ch of s) sum += parseInt(ch, 10)
    return sum % 5 === 0
}

const isValidSiret = (s: string): boolean => {
    if (!/^\d{14}$/.test(s)) return false
    if (/^0{14}$/.test(s)) return false
    if (isLaPosteSiret(s)) return true
    let sum = 0
    for (let i = 0; i < 14; i++) {
        const d = parseInt(s[i], 10)
        sum += i % 2 === 0 ? (d * 2 > 9 ? d * 2 - 9 : d * 2) : d
    }
    return sum % 10 === 0
}

// TVA UE : formats officiels par pays (VIES). Préfixe Grèce = "EL" alors que ISO 3166 = "GR" → mapping côté check
const VAT_FORMATS: Record<string, RegExp> = {
    AT: /^ATU\d{8}$/,
    BE: /^BE[0-1]\d{9}$/,
    BG: /^BG\d{9,10}$/,
    CY: /^CY\d{8}[A-Z]$/,
    CZ: /^CZ\d{8,10}$/,
    DE: /^DE\d{9}$/,
    DK: /^DK\d{8}$/,
    EE: /^EE\d{9}$/,
    EL: /^EL\d{9}$/,
    ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
    FI: /^FI\d{8}$/,
    FR: /^FR[0-9A-Z]{2}\d{9}$/,
    HR: /^HR\d{11}$/,
    HU: /^HU\d{8}$/,
    IE: /^IE\d[A-Z0-9+*]\d{5}[A-Z]{1,2}$/,
    IT: /^IT\d{11}$/,
    LT: /^LT(\d{9}|\d{12})$/,
    LU: /^LU\d{8}$/,
    LV: /^LV\d{11}$/,
    MT: /^MT\d{8}$/,
    NL: /^NL\d{9}B\d{2}$/,
    PL: /^PL\d{10}$/,
    PT: /^PT\d{9}$/,
    RO: /^RO\d{2,10}$/,
    SE: /^SE\d{10}01$/,
    SI: /^SI\d{8}$/,
    SK: /^SK\d{10}$/,
}

// Libellés lisibles par champ pour le récap d'erreurs (clé = attribut name)
const FIELD_LABELS: Record<string, string> = {
    pro_perso: 'Vous êtes (Professionnel ou Autre)',
    type_demande: 'En quoi pouvons-nous vous aider',
    taille_entreprise: "Taille d'entreprise",
    type_entreprise: "Type d'entreprise",
    carte_etudiant: 'Carte étudiant',
    attestation_retraite: 'Attestation de retraite',
    raison_sociale: 'Raison sociale',
    effectif: 'Effectif',
    civilite_dirigeant: 'Civilité du dirigeant',
    nom_dirigeant: 'Nom du dirigeant',
    prenom_dirigeant: 'Prénom du dirigeant',
    poste_dirigeant: 'Fonction du dirigeant',
    pays: 'Pays',
    adresse: 'Adresse',
    ville: 'Ville',
    etat: 'État',
    tva: 'Numéro de TVA',
    siret: 'SIRET',
    kbis: 'KBIS',
    civilite_contact: 'Civilité',
    nom_contact: 'Nom',
    prenom_contact: 'Prénom',
    email_contact: 'Email',
    telephone_contact: 'Téléphone',
    ine: 'Numéro INE',
    ecole_nom: 'École / Université',
    ecole_site: "Site Internet de l'école",
    etudes_duree: 'Durée des études',
    etudes_annee: 'Année en cours',
    message: 'Message',
    nb_developpeurs_windev_suite: 'Nombre de développeurs WINDEV Suite',
    ca: "Chiffre d'affaires annuel",
    code_postal: 'Code postal',
    diplome_libelle: 'Libellé du diplôme',
    nb_clients: 'Nombre de clients',
    clientele: 'Type de clientèle',
    mode_commercialisation: 'Mode de commercialisation',
    clients_direct_indirect: 'Clients en direct ou indirect',
}

export const initContactForm = (): void => {
    const form = document.getElementById('contact-form') as HTMLFormElement
    if (!form) return

    // Retour arrière après un envoi réussi : formulaire vierge (sinon saisie partielle remise par le navigateur, et
    // doublon au renvoi). Un envoi refusé revient par une navigation normale et garde sa saisie.
    const SENT_KEY = 'contact-form-sent'
    const wasSent = (): boolean => { try { return sessionStorage.getItem(SENT_KEY) === '1' } catch { return false } }
    const navType = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type
    if (wasSent()) {
        try { sessionStorage.removeItem(SENT_KEY) } catch { /* stockage indisponible */ }
        if (navType !== 'navigate') { window.location.replace(window.location.href); return }
    }
    form.addEventListener('submit', () => { try { sessionStorage.setItem(SENT_KEY, '1') } catch { /* stockage indisponible */ } })
    window.addEventListener('pageshow', e => { if (e.persisted && wasSent()) window.location.replace(window.location.href) })

    // Scroll vers le bloc d'erreurs si présent (la navbar sticky le cacherait sinon).
    // Le scroll-margin-top côté CSS compense la hauteur de la navbar.
    const errorBlock = document.querySelector<HTMLElement>('[data-form-errors]')
    if (errorBlock) {
        errorBlock.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const radiosProPerso = form.querySelectorAll<HTMLInputElement>('input[name="pro_perso"]')
    const selectDemande  = form.querySelector<HTMLSelectElement>('[name="type_demande"]')
    const selectType     = form.querySelector<HTMLSelectElement>('[name="type_entreprise"]')
    const selectTaille   = form.querySelector<HTMLSelectElement>('[name="taille_entreprise"]')
    const selectPays     = form.querySelector<HTMLSelectElement>('[name="pays"]')
    const inputTva       = form.querySelector<HTMLInputElement>('[name="tva"]')
    const inputSiret     = form.querySelector<HTMLInputElement>('[name="siret"]')
    const inputKbis      = form.querySelector<HTMLInputElement>('[name="kbis"]')
    const inputEffectif  = form.querySelector<HTMLInputElement>('[name="effectif"]')
    const inputTelephone = form.querySelector<HTMLInputElement>('[name="telephone_contact"]')
    const labelType      = form.querySelector<HTMLLabelElement>('label[for="type_entreprise"]')
    const requiredNotice = document.querySelector<HTMLElement>('[data-required-notice]')

    if (!selectDemande || !selectType) return

    // Marquer les champs initialement required pour pouvoir les remettre après masquage
    form.querySelectorAll<HTMLElement>('[required]').forEach(field => {
        field.dataset.required = 'true'
    })

    // Hidden injecté dynamiquement pour forcer la valeur de taille_entreprise selon type_entreprise :
    //   - administration → taille_entreprise=administration
    //   - etudiant / retraite / autre (particuliers) → taille_entreprise=aucun
    //   - autres cas → pas de hidden, le select est actif
    let hiddenTailleEntreprise: HTMLInputElement | null = null

    const getProPerso = (): string => {
        for (const r of radiosProPerso) if (r.checked) return r.value
        return ''
    }

    // Statut éducation attendu selon « Vous êtes » : un professionnel = établissement scolaire, un particulier = étudiant
    const expectedLicenceStatut = (): string => getProPerso() === 'particulier' ? 'etudiant' : 'ecole'

    // L'option « Demande de Licence Éducation » n'est proposée que si le statut est encore vide (il est demandé
    // APRÈS la demande dans le formulaire, donc on ne peut pas le présumer à ce stade) ou s'il correspond au
    // statut éducation. Pour tout statut incompatible (Retraité, ESN…), l'option est retirée du select.
    const licenceOption = selectDemande.querySelector<HTMLOptionElement>('option[value="demande_licence_education"]')
    const refreshLicenceOption = (): void => {
        if (!licenceOption) return
        const statut = selectType.value
        const allowed = statut === '' || statut === expectedLicenceStatut()
        licenceOption.hidden = !allowed
        licenceOption.disabled = !allowed
    }

    const filterTypeEntrepriseOptions = (proPerso: string): void => {
        if (!selectType) return
        // Affiche les options correspondant au pro_perso choisi, masque les autres
        const options = selectType.querySelectorAll<HTMLOptionElement>('option[data-pro-perso]')
        options.forEach(opt => {
            const matches = opt.dataset.proPerso === proPerso
            opt.hidden = !matches
            opt.disabled = !matches
        })
        // Si la valeur courante n'est plus visible, reset sur le placeholder
        const current = selectType.querySelector<HTMLOptionElement>(`option[value="${selectType.value}"]`)
        if (current && current.hidden) {
            selectType.value = ''
        }
    }

    const updateTailleEntrepriseHidden = (typeEntreprise: string): void => {
        // Force la valeur de taille_entreprise via un input hidden quand le select est masqué
        // (le hidden est placé à la fin du form, Laravel garde la dernière valeur du même name)
        const forcedValue =
            typeEntreprise === 'administration'
                ? 'administration'
                : typeEntreprise === 'independant'
                    ? 'micro_entreprise'
                    : (['etudiant', 'retraite', 'autre'].includes(typeEntreprise) ? 'aucun' : null)

        if (forcedValue) {
            if (!hiddenTailleEntreprise) {
                hiddenTailleEntreprise = document.createElement('input')
                hiddenTailleEntreprise.type = 'hidden'
                hiddenTailleEntreprise.name = 'taille_entreprise'
                form.appendChild(hiddenTailleEntreprise)
            }
            hiddenTailleEntreprise.value = forcedValue
        } else if (hiddenTailleEntreprise) {
            hiddenTailleEntreprise.remove()
            hiddenTailleEntreprise = null
        }
    }

    const updateVisibility = (): void => {
        const proPerso = getProPerso()
        const demande  = selectDemande.value
        // Licence Éducation : un particulier = étudiant, un professionnel = établissement scolaire (contenu d'une demande de devis).
        // La présélection du statut est posée aux transitions (changement de demande / pro-perso), pas ici, pour rester modifiable.
        const effectiveDemande = demande === 'demande_licence_education' && proPerso !== 'particulier' ? 'demande_devis' : demande
        const typeEnt  = selectType.value

        // Un indépendant / freelance est par nature 1 personne : taille forcée à micro et effectif à 1, verrouillés.
        // (select désactivé → ne se soumet pas ; la valeur micro part via le hidden injecté par updateTailleEntrepriseHidden)
        const isIndependant = typeEnt === 'independant'
        if (selectTaille) {
            if (isIndependant) selectTaille.value = 'micro_entreprise'
            selectTaille.disabled = isIndependant
        }
        if (inputEffectif) {
            if (isIndependant) {
                inputEffectif.value = '1'
                inputEffectif.readOnly = true
                inputEffectif.setCustomValidity('')
            } else {
                inputEffectif.readOnly = false
            }
        }

        const taille   = selectTaille?.value ?? 'aucun'
        const pays     = selectPays?.value ?? ''

        // Niveau 1 : type_demande → blocs data-form-show (demande effective : licence pro = devis)
        form.querySelectorAll<HTMLElement>('[data-form-show]').forEach(el => {
            const shows = (el.dataset.formShow ?? '').split(',').map(s => s.trim())
            const visible = shows.some(s => effectiveDemande === `demande_${s}`)
            el.classList.toggle('hidden', !visible)
        })
        // Niveau 2 : type_entreprise → blocs data-type-show (uploads carte_etudiant, attestation_retraite)
        form.querySelectorAll<HTMLElement>('[data-type-show]').forEach(el => {
            const shows = (el.dataset.typeShow ?? '').split(',').map(s => s.trim())
            el.classList.toggle('hidden', !shows.includes(typeEnt))
        })

        // Niveau 2 bis : type_entreprise → blocs data-type-hide (taille_entreprise masqué pour administration/etudiant/retraite/autre)
        form.querySelectorAll<HTMLElement>('[data-type-hide]').forEach(el => {
            const hides = (el.dataset.typeHide ?? '').split(',').map(s => s.trim())
            if (hides.includes(typeEnt)) {
                el.classList.add('hidden')
            }
        })

        // Cas spécifique taille_entreprise : masqué dès que pro_perso=particulier
        // (même quand type_entreprise est vide/placeholder, donc avant d'être dans la liste data-type-hide)
        const tailleBloc = selectTaille?.closest<HTMLElement>('[data-form-show]')
        if (tailleBloc && proPerso === 'particulier') {
            tailleBloc.classList.add('hidden')
        }

        // Niveau 3 : structure d'entreprise → blocs data-structure (raison sociale, effectif, dirigeant, infos légales)
        // Pour administration, taille_entreprise est forcée via hidden injecté : le select reste visuellement à 'aucun'.
        // On aligne sur la valeur effectivement soumise au serveur pour que hasStructure côté JS reflète la rule backend.
        const effectiveTaille = typeEnt === 'administration' ? 'administration' : taille
        const hasStructure = TYPES_ENTREPRISE_AVEC_STRUCTURE.includes(typeEnt) && TAILLES_AVEC_STRUCTURE.includes(effectiveTaille)
        form.querySelectorAll<HTMLElement>('[data-structure]').forEach(el => {
            el.classList.toggle('hidden', !hasStructure)
        })
        // CA : demandé aux entreprises avec structure, sauf administration
        form.querySelector<HTMLElement>('[data-ca-block]')?.classList.toggle('hidden', !(hasStructure && typeEnt !== 'administration'))

        // Effacer un message de validité résiduel sur l'effectif quand le bloc structure est masqué
        if (inputEffectif && !hasStructure) inputEffectif.setCustomValidity('')

        // Niveau 4 : pays → bloc état conditionnel
        form.querySelectorAll<HTMLElement>('[data-pays-show]').forEach(el => {
            const shows = (el.dataset.paysShow ?? '').split(',').map(s => s.trim())
            el.classList.toggle('hidden', !shows.includes(pays))
        })

        // Niveau 4 bis : pays + type_entreprise → KBIS (FR + type ∈ liste KBIS)
        const kbisVisible = PAYS_KBIS_REQUIRED.includes(pays) && TYPES_ENTREPRISE_KBIS.includes(typeEnt)
        form.querySelectorAll<HTMLElement>('[data-kbis-show]').forEach(el => {
            el.classList.toggle('hidden', !kbisVisible)
        })

        // Niveau 5 : TVA et SIRET conditionnels (visibilité + required selon pays et type_entreprise)
        // TVA : exige hasStructure (cohérent avec la validation serveur : un indépendant taille=aucun n'a pas de TVA)
        // SIRET : pas de check hasStructure (un indépendant FR a un SIRET même sans structure déclarée)
        const tvaVisible   = hasStructure && PAYS_TVA_REQUIRED.includes(pays) && TYPES_ENTREPRISE_REQUIRED_TVA.includes(typeEnt)
        const siretVisible = PAYS_SIRET_REQUIRED.includes(pays) && TYPES_ENTREPRISE_REQUIRED_SIRET.includes(typeEnt)

        const tvaWrapper   = form.querySelector<HTMLElement>('[data-tva-wrapper]')
        const siretWrapper = form.querySelector<HTMLElement>('[data-siret-wrapper]')
        tvaWrapper?.classList.toggle('hidden', !tvaVisible)
        siretWrapper?.classList.toggle('hidden', !siretVisible)
        form.querySelector<HTMLElement>('[data-tva-checks]')?.classList.toggle('hidden', !tvaVisible)

        // La coche « auto-entrepreneur non assujetti » ne concerne que le développeur indépendant (freelance)
        const freelanceOnly = form.querySelector<HTMLElement>('[data-freelance-only]')
        if (freelanceOnly) {
            const showFreelance = typeEnt === 'independant'
            freelanceOnly.classList.toggle('hidden', !showFreelance)
            const cbFreelance = freelanceOnly.querySelector<HTMLInputElement>('input')
            if (!showFreelance && cbFreelance?.checked) cbFreelance.checked = false
        }

        // La coche « secteur public » ne concerne que l'organisme public / administration
        const publicOnly = form.querySelector<HTMLElement>('[data-public-only]')
        if (publicOnly) {
            const showPublic = typeEnt === 'administration'
            publicOnly.classList.toggle('hidden', !showPublic)
            const cbPublic = publicOnly.querySelector<HTMLInputElement>('input')
            if (!showPublic && cbPublic?.checked) cbPublic.checked = false
        }

        // La coche « association non assujettie » ne concerne que l'association / organisme à but non lucratif
        const associationOnly = form.querySelector<HTMLElement>('[data-association-only]')
        if (associationOnly) {
            const showAssociation = typeEnt === 'association'
            associationOnly.classList.toggle('hidden', !showAssociation)
            const cbAssociation = associationOnly.querySelector<HTMLInputElement>('input')
            if (!showAssociation && cbAssociation?.checked) cbAssociation.checked = false
        }

        // Si une coche « pas de TVA » (secteur public / auto-entrepreneur / association non assujettis) est cochée, la TVA n'est plus obligatoire
        const tvaExempt = form.querySelector<HTMLInputElement>('[data-tva-exempt]:checked') !== null
        if (inputTva) {
            if (tvaVisible && !tvaExempt) {
                inputTva.setAttribute('required', ''); inputTva.dataset.required = 'true'
            } else {
                inputTva.removeAttribute('required'); delete inputTva.dataset.required
                inputTva.setCustomValidity('')
                if (tvaExempt) inputTva.value = ''
            }
        }
        if (inputSiret) {
            if (siretVisible) { inputSiret.setAttribute('required', ''); inputSiret.dataset.required = 'true' }
            else { inputSiret.removeAttribute('required'); delete inputSiret.dataset.required; inputSiret.setCustomValidity('') }
        }

        // Bloc "Informations légales" visible si TVA ou SIRET l'est (plus dépendant de hasStructure)
        // Permet par exemple de montrer SIRET+TVA à un indépendant FR taille=aucun
        const infosLegalesBloc = form.querySelector<HTMLElement>('[data-infos-legales]')
        if (infosLegalesBloc) {
            infosLegalesBloc.classList.toggle('hidden', !tvaVisible && !siretVisible)
        }

        // Force la valeur de taille_entreprise via hidden pour admin et particuliers
        updateTailleEntrepriseHidden(typeEnt)

        // Bloc Applications (qualification B2B) : réservé au Professionnel
        const appsBlock = form.querySelector<HTMLElement>('[data-pro-only]')
        if (appsBlock && proPerso === 'particulier') {
            appsBlock.classList.add('hidden')
            form.querySelectorAll<HTMLInputElement>('[data-apps-toggle]').forEach(cb => { cb.checked = false })
        }
        form.querySelectorAll<HTMLInputElement>('[data-apps-toggle]').forEach(cb => applyAppsZoneState(cb.dataset.appsToggle as string))

        // Désactive required sur les champs masqués (sinon le formulaire ne se soumet pas)
        form.querySelectorAll<HTMLElement>('[data-required]').forEach(field => {
            const parentBloc = field.closest('[data-form-show], [data-type-show], [data-type-hide], [data-structure], [data-pays-show], [data-kbis-show]')
            if (parentBloc && parentBloc.classList.contains('hidden')) {
                field.removeAttribute('required')
            } else {
                field.setAttribute('required', '')
            }
        })

        // Notice "Saisie obligatoire *" si au moins un required visible
        if (requiredNotice) {
            const anyRequired = form.querySelector('[required]') !== null
            requiredNotice.classList.toggle('hidden', !anyRequired)
        }

        // Retrait / rétablissement de l'option « Demande de Licence Éducation » selon le statut courant
        refreshLicenceOption()
    }

    const updateTypeLabel = (proPerso: string): void => {
        if (!labelType) return
        const labelPro = labelType.dataset.labelPro
        const labelPerso = labelType.dataset.labelPerso
        if (!labelPro || !labelPerso) return
        labelType.textContent = proPerso === 'particulier' ? labelPerso : labelPro
    }

    // Filtrage initial des options type_entreprise selon pro_perso initial
    const onProPersoChange = (): void => {
        const proPerso = getProPerso()
        // Au changement de "Vous êtes", la combo "En quoi pouvons-nous vous aider" est remise à sa valeur par
        // défaut d'arrivée du formulaire, pour éviter une demande devenue incompatible avec le nouveau profil.
        selectDemande.value = 'demande_rappel'
        filterTypeEntrepriseOptions(proPerso)
        updateTypeLabel(proPerso)
        updateVisibility()
    }

    // Licence Éducation : présélection du statut (étudiant / école) et proposition de bascule si on le change
    const licenceSwitchTrigger = document.getElementById('licence-switch-trigger')
    const licenceSwitchLabel = document.querySelector<HTMLElement>('[data-licence-switch-label]')
    let pendingLicenceFallback = ''
    const onDemandeChange = (): void => {
        if (selectDemande.value === 'demande_licence_education') selectType.value = expectedLicenceStatut()
        updateVisibility()
    }
    const onTypeChange = (): void => {
        if (selectDemande.value === 'demande_licence_education' && selectType.value !== expectedLicenceStatut()) {
            const particulier = getProPerso() === 'particulier'
            pendingLicenceFallback = particulier ? 'demande_information' : 'demande_devis'
            // Libellé repris de l'option de la liste, déjà traduite dans la langue de la page
            const fallbackOption = selectDemande.querySelector<HTMLOptionElement>(`option[value="${pendingLicenceFallback}"]`)
            if (licenceSwitchLabel && fallbackOption) licenceSwitchLabel.textContent = fallbackOption.textContent?.trim() ?? ''
            if (licenceSwitchTrigger) { licenceSwitchTrigger.click(); return }
        }
        updateVisibility()
    }
    document.querySelectorAll<HTMLElement>('[data-licence-switch-yes]').forEach(btn => btn.addEventListener('click', () => {
        if (pendingLicenceFallback) selectDemande.value = pendingLicenceFallback
        updateVisibility()
    }))
    document.querySelectorAll<HTMLElement>('[data-licence-switch-no]').forEach(btn => btn.addEventListener('click', () => {
        selectType.value = expectedLicenceStatut()
        updateVisibility()
    }))

    radiosProPerso.forEach(r => r.addEventListener('change', onProPersoChange))
    selectDemande.addEventListener('change', onDemandeChange)
    selectType.addEventListener('change', onTypeChange)
    selectTaille?.addEventListener('change', updateVisibility)
    selectPays?.addEventListener('change', updateVisibility)
    // Coches « pas de TVA » → recalcul de l'obligation TVA
    form.querySelectorAll<HTMLInputElement>('[data-tva-exempt]').forEach(c => c.addEventListener('change', updateVisibility))

    // Effectif : restreindre la saisie aux chiffres uniquement (cohérent avec inputmode=numeric côté Blade)
    // Couvre la frappe ET le copier-coller via l'event input
    inputEffectif?.addEventListener('input', () => {
        inputEffectif.value = inputEffectif.value.replace(/\D/g, '')
    })

    // Nombres entiers attendus par le serveur : chiffres seuls, sans zéro en tête (« 1 000 », « 05 » y sont refusés).
    // Écoute déléguée car les lignes d'application sont insérées dynamiquement.
    const DIGITS_ONLY = [
        '[name="effectif"]', '[name="nb_developpeurs_windev_suite"]', '[name="nb_clients"]',
        '[name="etudes_duree"]', '[name="etudes_annee"]',
        '[data-field="nb_executables"]', '[data-field="nb_users"]',
    ].join(', ')
    form.addEventListener('input', e => {
        const field = (e.target as HTMLElement).closest<HTMLInputElement>(DIGITS_ONLY)
        if (field) field.value = field.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '')
    })

    // Montants et volumes : « 50 k », « 1,5 M€ », « 1.500.000 » sont convertis en entier quand on quitte le champ,
    // puis de nouveau au clic sur « Envoyer », avant les contrôles du navigateur. Une saisie non reconnue est
    // signalée au lieu d'être tronquée : « 1,5 M€ » ne doit jamais devenir 15.
    const QUANTITY_FIELDS: { selector: string; roundDecimals: boolean; message: string }[] = [
        { selector: '[name="ca"]', roundDecimals: true,
            message: "montant non reconnu, en euros (ex.\u00A01\u00A0500\u00A0000, 50\u00A0k ou 1,5\u00A0M€)" },
        { selector: '[data-field="lignes_code"]', roundDecimals: false,
            message: 'nombre non reconnu (ex.\u00A050\u00A0000 ou 50\u00A0k)' },
    ]
    const quantityRuleFor = (el: EventTarget | null) =>
        el instanceof HTMLInputElement ? QUANTITY_FIELDS.find(q => el.matches(q.selector)) : undefined
    // CA faible (moins de 10 000 €, seuil repris du message de la vue) : on prévient sans bloquer, sous le champ tant
    // que le montant ne change pas, et par un rappel temporaire en bas à droite (au changement de champ et à l'envoi).
    const CA_WARNING_THRESHOLD = 10_000
    const caWarning = form.querySelector<HTMLElement>('[data-ca-warning]')
    const caToast = document.querySelector<HTMLElement>('[data-ca-toast]')
    let caToastTimer: number | undefined
    let caWarnedValue: string | null = null
    const updateCaWarning = (field: HTMLInputElement, remind: boolean): void => {
        const low = field.offsetParent !== null && field.validity.valid && field.value !== ''
            && Number(field.value) < CA_WARNING_THRESHOLD
        if (caWarning) caWarning.hidden = !low
        if (low) field.setAttribute('aria-describedby', 'ca-warning')
        else field.removeAttribute('aria-describedby')
        if (!low) { caWarnedValue = null; return }
        // Rappel déjà montré pour ce montant : pas de répétition au simple changement de champ
        if (!caToast || (!remind && field.value === caWarnedValue)) return
        caWarnedValue = field.value
        caToast.hidden = false
        window.clearTimeout(caToastTimer)
        caToastTimer = window.setTimeout(() => { caToast.hidden = true }, 6000)
    }

    const normalizeQuantity = (field: HTMLInputElement, remind = false): void => {
        const rule = quantityRuleFor(field)
        if (!rule) return
        // Champ masqué : pas de message résiduel qui bloquerait l'envoi sans être visible
        if (field.offsetParent === null) field.setCustomValidity('')
        else {
            const result = parseQuantity(field.value, { roundDecimals: rule.roundDecimals, locale: document.documentElement.lang })
            if (result.ok) field.value = result.value
            field.setCustomValidity(result.ok ? '' : rule.message)
        }
        if (field.name === 'ca') updateCaWarning(field, remind)
        if (hasAttemptedSubmit) scheduleRender()
    }
    form.addEventListener('focusout', e => { if (quantityRuleFor(e.target)) normalizeQuantity(e.target as HTMLInputElement) })
    // Pendant la correction, message et avertissement s'effacent ; ils reviennent au changement de champ si besoin
    form.addEventListener('input', e => {
        if (!quantityRuleFor(e.target)) return
        const field = e.target as HTMLInputElement
        field.setCustomValidity('')
        if (field.name === 'ca' && caWarning) caWarning.hidden = true
    })
    // Le bouton « Envoyer » est hors du <form> (attribut form) : écoute sur le document, en capture pour passer avant
    // la validation native. La touche Entrée dans un champ déclenche aussi ce clic.
    document.addEventListener('click', e => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement | HTMLInputElement>('button[type="submit"], input[type="submit"]')
        if (btn?.form !== form) return
        form.querySelectorAll<HTMLInputElement>(QUANTITY_FIELDS.map(q => q.selector).join(', '))
            .forEach(field => normalizeQuantity(field, true))
    }, true)

    // SIRET : digits only + slice à 14 (taille SIRET officielle)
    // Le maxlength=17 côté Blade tolère un paste avec espaces ("XXX XXX XXX XXXXX"), le filtre strippe les espaces
    // puis le slice garantit qu'on ne dépasse jamais 14 chiffres, même si le paste contenait plus de chiffres
    inputSiret?.addEventListener('input', () => {
        inputSiret.value = inputSiret.value.replace(/\D/g, '').slice(0, 14)
    })

    // TVA Intra Com : alphanumérique uppercase auto
    // Format officiel : préfixe pays ISO-2 (2 lettres) + chiffres, parfois lettres (ex: FR12345678901, BE0123456789)
    // Strip espaces, tirets, ponctuation. Conversion uppercase (les VAT numbers sont toujours en majuscules).
    inputTva?.addEventListener('input', () => {
        inputTva.value = inputTva.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
    })

    // Téléphone : chiffres + caractères de mise en forme courants
    // Autorise : 0-9, espace, +, -, ( ), . (formats français 06.12.34.56.78, internationaux +33 6 12 34 56 78, etc.)
    inputTelephone?.addEventListener('input', () => {
        inputTelephone.value = inputTelephone.value.replace(/[^\d +\-().]/g, '')
    })

    // SIRET : check format + Luhn (avec exception La Poste, anti-tout-zéros) au blur
    // Le filtre input garantit déjà digits-only et longueur <= 14, le blur valide la cohérence INSEE
    inputSiret?.addEventListener('blur', () => {
        const value = inputSiret.value.trim()
        if (value === '') { inputSiret.setCustomValidity(''); return }
        inputSiret.setCustomValidity(isValidSiret(value) ? '' : 'numéro invalide')
    })

    // TVA : check format pays au blur (regex offline VAT_FORMATS). Re-validation aussi au changement de pays
    // Mapping GR (ISO 3166) → EL (VIES) car la TVA grecque commence par EL
    const validateTva = (): void => {
        if (!inputTva) return
        const value = inputTva.value.trim()
        if (value === '') { inputTva.setCustomValidity(''); return }
        const paysIso = selectPays?.value ?? ''
        const vatPrefix = paysIso === 'GR' ? 'EL' : paysIso
        const pattern = VAT_FORMATS[vatPrefix]
        if (!pattern) { inputTva.setCustomValidity(''); return }
        inputTva.setCustomValidity(pattern.test(value) ? '' : 'numéro invalide')
    }
    inputTva?.addEventListener('blur', validateTva)
    selectPays?.addEventListener('change', validateTva)

    // E-mail : le serveur vérifie que le domaine existe (DNS). Miroir hors ligne des seuls cas refusés à coup sûr :
    // extensions réservées, et fautes de frappe sur l'extension qui ne correspondent à aucune extension réelle.
    const inputEmail = form.querySelector<HTMLInputElement>('[name="email_contact"]')
    const EMAIL_EXTENSION_INEXISTANTE = /\.(invalid|test|example|localhost|local|con|cmo|ocm|vom|cpm|xom|comm|frr)$/i
    const validateEmail = (): void => {
        if (!inputEmail) return
        const domaine = inputEmail.value.trim().split('@')[1] ?? ''
        inputEmail.setCustomValidity(EMAIL_EXTENSION_INEXISTANTE.test(domaine) ? 'adresse invalide (domaine inexistant)' : '')
    }
    inputEmail?.addEventListener('input', validateEmail)
    validateEmail() // valeur remise après une erreur serveur

    // Effectif : min 1 + cohérence avec taille_entreprise (seuils des libellés du select).
    // Re-validé sur changement de taille/type/pro_perso car le hidden injecté force taille=administration ou aucun
    // dans ces cas → message résiduel à effacer pour ne pas bloquer un submit silencieusement sur un champ masqué.
    const validateEffectif = (): void => {
        if (!inputEffectif) return
        const value = inputEffectif.value.trim()
        if (value === '') { inputEffectif.setCustomValidity(''); return }

        const n = parseInt(value, 10)
        if (n < 1) {
            inputEffectif.setCustomValidity('doit être au moins 1')
            return
        }

        // hidden prioritaire car pour administration le select reste à 'aucun' visuellement
        const taille = hiddenTailleEntreprise?.value ?? selectTaille?.value ?? 'aucun'
        const range = TAILLE_EFFECTIF_RANGES[taille]
        if (!range) { inputEffectif.setCustomValidity(''); return }

        const ok = n >= range.min && (range.max === null || n <= range.max)
        inputEffectif.setCustomValidity(ok ? '' : 'à vérifier avec la taille d\'entreprise choisie')
    }
    inputEffectif?.addEventListener('blur', validateEffectif)
    selectTaille?.addEventListener('change', validateEffectif)
    selectType.addEventListener('change', validateEffectif)
    radiosProPerso.forEach(r => r.addEventListener('change', validateEffectif))

    // Affichage du nom de fichier sur les uploads
    const syncUpload = (input: HTMLInputElement): void => {
        const label = form.querySelector<HTMLElement>(`[data-upload-label="${input.name}"]`)
        if (label && input.files?.length) {
            label.textContent = input.files[0].name
        }
        // Même plafond que le serveur (5 Mo)
        const tooBig = (input.files?.[0]?.size ?? 0) > 5 * 1024 * 1024
        input.setCustomValidity(tooBig ? 'fichier trop volumineux (5\u00A0Mo maximum)' : '')
    }
    form.querySelectorAll<HTMLInputElement>('input[type="file"]').forEach(input => {
        input.addEventListener('change', () => syncUpload(input))
        // Retour arrière : le navigateur remet le fichier sans émettre « change »
        syncUpload(input)
        window.addEventListener('pageshow', () => syncUpload(input))
    })

    // Inventaire d'applications : les cases interne/commercial déplient chacune une liste de lignes répétables.
    // Champs obligatoires quand la zone est cochée (sauf « lignes de code »), désactivés sinon : on ne soumet
    // pas des données saisies puis décochées, et on ne bloque pas la validation sur des champs masqués.
    const appsRowTemplate = document.querySelector<HTMLTemplateElement>('template[data-apps-row-template]')
    const APPS_REQUIRED_FIELDS = ['nom', 'nb_executables', 'nb_users', 'agl'] // « lignes de code » optionnel ; « bdd_type »/« bdd_acces » gérés à part (groupes de cases)

    const setControlState = (field: HTMLInputElement | HTMLSelectElement, active: boolean, required: boolean): void => {
        field.disabled = !active
        if (active && required) field.setAttribute('required', '')
        else field.removeAttribute('required')
    }

    // « Base de données » : 2 groupes multi-cochés par application (Type de données / Technologie d'accès).
    // Le required natif ne couvre pas « au moins une du groupe », donc on porte cette contrainte sur la 1re
    // case du groupe via setCustomValidity → elle remonte dans le récap natif (préfixé par le libellé du groupe
    // via labelFor/messageFor, qui identifie l'usage, l'application et le groupe → message générique ici).
    const BDD_GROUP_MSG = 'cochez au moins une case (ou «\u00A0Aucune\u00A0»)'
    const syncBddGroup = (group: HTMLElement): void => {
        const checks = [...group.querySelectorAll<HTMLInputElement>('[data-bdd]')]
        if (!checks.length) return
        const active = !checks[0].disabled
        const missing = active && !checks.some(c => c.checked)
        checks[0].setCustomValidity(missing ? BDD_GROUP_MSG : '')
        // Champ « Précisez » de « Autre » : saisissable dès que la zone est active (saisir coche « Autre » via le
        // listener input). Vidé seulement si la zone est coupée ou si « Autre » est décoché → cohérence texte ⇔ Autre.
        const autreInput = group.querySelector<HTMLInputElement>('[data-bdd-autre]')
        if (autreInput) {
            const autreChecked = checks.some(c => c.value === 'autre' && c.checked)
            autreInput.disabled = !active
            if (!active || !autreChecked) autreInput.value = ''
            // « Autre » coché → « Précisez » obligatoire et non vide d'espaces, comme côté serveur
            if (active && autreChecked) { autreInput.setAttribute('required', ''); autreInput.pattern = '.*\\S.*' }
            else { autreInput.removeAttribute('required'); autreInput.removeAttribute('pattern') }
        }
    }

    const applyAppsZoneState = (which: string): void => {
        const active = form.querySelector<HTMLInputElement>(`[data-apps-toggle="${which}"]`)?.checked ?? false
        const zone = form.querySelector<HTMLElement>(`[data-apps-zone="${which}"]`)
        if (!zone) return
        zone.classList.toggle('hidden', !active)
        // Champs « une fois » (zone commerciale : nombre de clients + 3 listes) : tous obligatoires si actif
        zone.querySelectorAll<HTMLInputElement | HTMLSelectElement>(':scope > input, :scope > select').forEach(f => setControlState(f, active, true))
        // Champs des lignes d'application : obligatoires sauf « lignes de code »
        zone.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-field]').forEach(f =>
            setControlState(f, active, APPS_REQUIRED_FIELDS.includes(f.dataset.field ?? '')))
        // Cases « Base de données » : activées avec la zone, validité recalculée pour chaque groupe (Type / Accès)
        zone.querySelectorAll<HTMLInputElement>('[data-bdd]').forEach(cb => { cb.disabled = !active })
        zone.querySelectorAll<HTMLElement>('[data-bdd-group]').forEach(syncBddGroup)
    }

    const reindexApps = (which: string): void => {
        form.querySelector<HTMLElement>(`[data-apps-list="${which}"]`)
            ?.querySelectorAll<HTMLElement>('[data-apps-row]').forEach((row, i) => {
                const title = row.querySelector<HTMLElement>('[data-apps-title]')
                // Libellé traduit porté par l'attribut (rendu côté serveur) : seul le numéro est ajouté ici.
                if (title) title.textContent = `${title.dataset.appsTitle ?? ''} ${i + 1}`.trim()
                row.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-field]').forEach(field => {
                    field.name = `apps_${which}[${i}][${field.dataset.field}]`
                    field.id = field.name // les composants DS posent id=name (placeholder) → on garantit l'unicité par ligne
                })
                // 2 groupes « Base de données » par application (Type / Technologie d'accès) : name dérivé de
                // data-bdd-group (bdd_type / bdd_acces), en tableau car plusieurs choix possibles par groupe.
                row.querySelectorAll<HTMLElement>('[data-bdd-group]').forEach(group => {
                    const key = group.dataset.bddGroup
                    group.querySelectorAll<HTMLInputElement>('[data-bdd]').forEach(cb => {
                        cb.name = `apps_${which}[${i}][${key}][]`
                    })
                    group.querySelectorAll<HTMLInputElement>('[data-bdd-autre]').forEach(inp => {
                        inp.name = `apps_${which}[${i}][${key}_autre]`
                        inp.id = inp.name // idem : id unique par ligne (le composant DS pose id=name)
                    })
                })
            })
    }
    const addAppRow = (which: string): void => {
        const list = form.querySelector<HTMLElement>(`[data-apps-list="${which}"]`)
        if (!appsRowTemplate || !list) return
        list.appendChild(appsRowTemplate.content.cloneNode(true))
        reindexApps(which)
        applyAppsZoneState(which) // la nouvelle ligne hérite de l'état actif + required
    }

    const onAppsToggle = (which: string): void => {
        applyAppsZoneState(which)
        const active = form.querySelector<HTMLInputElement>(`[data-apps-toggle="${which}"]`)?.checked ?? false
        const list = form.querySelector<HTMLElement>(`[data-apps-list="${which}"]`)
        // À l'ouverture d'une zone, afficher un premier bloc application si la liste est vide
        if (active && list && list.children.length === 0) addAppRow(which)
        // Espace au-dessus de « Usage commercial » uniquement quand « Usage interne » est coché
        if (which === 'interne') {
            const commLabel = form.querySelector<HTMLInputElement>('[data-apps-toggle="commercial"]')?.closest<HTMLElement>('label')
            if (commLabel) commLabel.style.marginTop = active ? '1.5rem' : ''
        }
    }

    // Retour d'erreur serveur : les lignes d'application étant clonées en JS, la vue ne peut pas les pré-remplir ;
    // elle transmet la saisie dans data-apps-old. Recréées avant l'init des zones pour éviter une ligne vide en plus.
    const restoreApps = (which: string): void => {
        const list = form.querySelector<HTMLElement>(`[data-apps-list="${which}"]`)
        let saved: unknown
        try { saved = JSON.parse(list?.dataset.appsOld ?? '[]') } catch { saved = [] }
        if (!list || !Array.isArray(saved) || saved.length === 0) return
        saved.forEach(() => addAppRow(which))
        list.querySelectorAll<HTMLElement>('[data-apps-row]').forEach((row, i) => {
            const app = (saved as Record<string, unknown>[])[i] ?? {}
            row.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-field]').forEach(field => {
                const value = app[field.dataset.field ?? '']
                if (typeof value === 'string' || typeof value === 'number') field.value = String(value)
            })
            row.querySelectorAll<HTMLElement>('[data-bdd-group]').forEach(group => {
                const key = group.dataset.bddGroup ?? ''
                const checked = Array.isArray(app[key]) ? app[key] as unknown[] : []
                group.querySelectorAll<HTMLInputElement>('[data-bdd]').forEach(cb => { cb.checked = checked.includes(cb.value) })
                const autreInput = group.querySelector<HTMLInputElement>('[data-bdd-autre]')
                const precision = app[`${key}_autre`]
                if (autreInput && typeof precision === 'string') autreInput.value = precision
                syncBddGroup(group)
            })
        })
    }
    restoreApps('interne')
    restoreApps('commercial')

    form.querySelectorAll<HTMLInputElement>('[data-apps-toggle]').forEach(cb => {
        const which = cb.dataset.appsToggle as string
        cb.addEventListener('change', () => onAppsToggle(which))
        onAppsToggle(which) // état initial : désactive les champs des zones décochées au chargement
    })

    // Pré-cochage de l'usage par défaut selon le type d'entreprise. corporate/administration/association/cabinet/école
    // → usage interne ; ESN/ISV/freelance → usage commercial ; revendeur/distributeur → rien (vente/redistribution,
    // pas forcément de dév). On COCHE l'usage par défaut à chaque (re)choix du type — même si l'utilisateur l'avait
    // décoché entre-temps — mais on ne DÉCOCHE jamais l'autre (non destructif : une zone déjà remplie n'est pas vidée).
    const USAGE_DEFAULT: Record<string, string> = {
        corpo: 'interne', administration: 'interne', association: 'interne', /*cabinet: 'interne',*/ ecole: 'interne',
        esn: 'commercial', isv: 'commercial', independant: 'commercial',
    }
    const applyUsageDefault = (): void => {
        if (getProPerso() === 'particulier') return
        const def = USAGE_DEFAULT[selectType.value] ?? '' // '' = revendeur / type sans défaut → ne rien toucher
        if (!def) return
        const cb = form.querySelector<HTMLInputElement>(`[data-apps-toggle="${def}"]`)
        if (cb && !cb.checked) {
            cb.checked = true
            onAppsToggle(def) // ouvre la zone (1re ligne d'application ajoutée si la liste est vide)
        }
    }
    selectType.addEventListener('change', applyUsageDefault)
    // Init : pose le défaut si aucun usage n'est déjà coché (on respecte un état restauré par old() après erreur de validation).
    if (!form.querySelector('[data-apps-toggle]:checked')) applyUsageDefault()
    form.querySelectorAll<HTMLElement>('[data-apps-add]').forEach(btn => {
        // Le clic « Ajouter » ne déclenche ni input ni change → rafraîchir le récap manuellement
        btn.addEventListener('click', () => { addAppRow(btn.dataset.appsAdd as string); scheduleRender() })
    })
    form.addEventListener('click', e => {
        const removeBtn = (e.target as HTMLElement).closest<HTMLElement>('[data-apps-remove]')
        if (!removeBtn) return
        const which = removeBtn.closest<HTMLElement>('[data-apps-list]')?.dataset.appsList
        removeBtn.closest<HTMLElement>('[data-apps-row]')?.remove()
        if (which) reindexApps(which)
        scheduleRender() // idem : retrait d'une ligne → mettre à jour les lignes du récap
    })
    // « Base de données » : « Aucune » exclusive des autres choix DANS son groupe, puis recalcul de la validité du groupe
    form.addEventListener('change', e => {
        const cb = (e.target as HTMLElement).closest<HTMLInputElement>('[data-bdd]')
        if (!cb) return
        const group = cb.closest<HTMLElement>('[data-bdd-group]')
        if (!group) return
        if (cb.dataset.bddNone !== undefined && cb.checked) {
            group.querySelectorAll<HTMLInputElement>('[data-bdd]').forEach(o => { if (o !== cb) o.checked = false })
        } else if (cb.checked) {
            const none = group.querySelector<HTMLInputElement>('[data-bdd][data-bdd-none]')
            if (none) none.checked = false
        }
        syncBddGroup(group)
        // « Autre » coché → curseur directement dans « Précisez »
        if (cb.value === 'autre' && cb.checked) group.querySelector<HTMLInputElement>('[data-bdd-autre]')?.focus()
    })
    // Entrer dans « Précisez » (clic ou tabulation) coche automatiquement « Autre » et décoche « Aucune » :
    // entrer dans le champ équivaut à cliquer la case « Autre » ou son libellé.
    form.addEventListener('focusin', e => {
        const input = (e.target as HTMLElement).closest<HTMLInputElement>('[data-bdd-autre]')
        if (!input) return
        const group = input.closest<HTMLElement>('[data-bdd-group]')
        if (!group) return
        const autre = group.querySelector<HTMLInputElement>('[data-bdd][value="autre"]')
        const none = group.querySelector<HTMLInputElement>('[data-bdd][data-bdd-none]')
        if (autre && !autre.checked) autre.checked = true
        if (none && none.checked) none.checked = false
        syncBddGroup(group)
    })

    // Une micro-ESN à 1 effectif est très probablement un développeur indépendant : on propose la bascule
    const freelanceTrigger = document.getElementById('freelance-trigger')
    let freelanceAsked = false
    const maybeAskFreelance = (): void => {
        if (!freelanceTrigger || freelanceAsked) return
        if (selectType.value === 'esn' && selectTaille?.value === 'micro_entreprise' && inputEffectif?.value.trim() === '1') {
            freelanceAsked = true
            freelanceTrigger.click()
        }
    }
    inputEffectif?.addEventListener('blur', maybeAskFreelance)
    selectType.addEventListener('change', () => { freelanceAsked = false })
    document.querySelectorAll<HTMLElement>('[data-freelance-yes]').forEach(btn => btn.addEventListener('click', () => {
        selectType.value = 'independant'
        selectType.dispatchEvent(new Event('change'))
    }))

    // --- Récap des champs à compléter, au-dessus du bouton « Envoyer » ---
    // La validation native reste active : au clic « Envoyer » sur un form invalide, le navigateur garde
    // sa bulle + le focus sur le 1er champ. En parallèle on écoute l'event `invalid` (en capture, car il
    // ne remonte pas) pour lister TOUS les champs en défaut sous le bouton. La liste se vide en live.
    const errorSummary = document.querySelector<HTMLElement>('[data-form-error-summary]')
    const errorSummaryList = errorSummary?.querySelector<HTMLElement>('[data-form-error-summary-list]')
    let hasAttemptedSubmit = false
    let renderScheduled = false

    type Validatable = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    // Libellés des champs d'une ligne d'application (name dynamique apps_<which>[i][field])
    const APPS_FIELD_LABELS: Record<string, string> = {
        nom: 'Nom',
        nb_executables: "Nombre d'exécutables",
        nb_users: "Nombre d'utilisateurs",
        lignes_code: 'Nombre de lignes de code',
        agl: 'AGL PCSoft',
        bdd_type: 'Type de données accédées',
        bdd_acces: "Technologie d'accès à la Base de données",
        bdd_type_autre: 'Type de données accédées, précisez «\u00A0Autre\u00A0»',
        bdd_acces_autre: "Technologie d'accès à la Base de données, précisez «\u00A0Autre\u00A0»",
    }
    const labelFor = (field: Validatable): string => {
        if (FIELD_LABELS[field.name]) return FIELD_LABELS[field.name]
        const m = field.name.match(/^apps_(interne|commercial)\[(\d+)\]\[(\w+)\](?:\[\])?$/)
        if (m) {
            const usage = m[1] === 'interne' ? 'Usage interne' : 'Usage commercial'
            return `${usage}, application ${parseInt(m[2], 10) + 1}, ${APPS_FIELD_LABELS[m[3]] ?? m[3]}`
        }
        return field.name
    }

    // Espaces insécables (\u00A0) avant « : » et dans les guillemets : la ponctuation ne passe jamais seule à la ligne.
    const messageFor = (field: Validatable): string => {
        const v = field.validity
        // Message maison posé via setCustomValidity : préfixé par le libellé du champ comme les autres lignes (la bulle
        // native, affichée contre le champ, garde le message seul).
        if (v.customError) return `${labelFor(field)}\u00A0: ${field.validationMessage}`
        if (v.valueMissing) {
            return field instanceof HTMLInputElement && field.type === 'file'
                ? `${labelFor(field)}\u00A0: fichier à joindre`
                : `${labelFor(field)}\u00A0: à renseigner`
        }
        if (v.typeMismatch || v.patternMismatch) return `${labelFor(field)}\u00A0: format invalide`
        return `${labelFor(field)}\u00A0: à vérifier`
    }

    const renderErrorSummary = (): void => {
        if (!errorSummary || !errorSummaryList || !hasAttemptedSubmit) return
        const seen = new Set<string>()
        const invalids: Validatable[] = []
        form.querySelectorAll<Validatable>(':invalid').forEach(field => {
            // Ignore les champs sans name, désactivés, ou dans un bloc masqué (non rendus)
            if (!field.name || field.disabled || field.offsetParent === null) return
            if (seen.has(field.name)) return
            seen.add(field.name)
            invalids.push(field)
        })
        errorSummaryList.replaceChildren()
        if (invalids.length === 0) {
            errorSummary.hidden = true
            return
        }
        invalids.forEach(field => {
            const li = document.createElement('li')
            li.className = 'flex items-start gap-2 break-inside-avoid'
            const dot = document.createElement('span')
            dot.setAttribute('aria-hidden', 'true')
            dot.textContent = '•'
            dot.className = 'shrink-0'
            const btn = document.createElement('button')
            btn.type = 'button'
            btn.dataset.errorJump = field.name
            btn.className = 'flex-1 text-left underline underline-offset-2 hover:no-underline cursor-pointer'
            btn.textContent = messageFor(field)
            li.append(dot, btn)
            errorSummaryList.appendChild(li)
        })
        errorSummary.hidden = false
    }

    function scheduleRender(): void {
        if (renderScheduled) return
        renderScheduled = true
        queueMicrotask(() => { renderScheduled = false; renderErrorSummary() })
    }

    // `invalid` se déclenche sur chaque champ en défaut lors d'une tentative d'envoi (puis le navigateur
    // affiche sa bulle sur le 1er). queueMicrotask regroupe la rafale en un seul rendu après coup.
    form.addEventListener('invalid', () => {
        hasAttemptedSubmit = true
        scheduleRender()
    }, true)

    // Après la 1re tentative, la liste se met à jour à chaque correction. Les messages SIRET/TVA/effectif
    // étant posés au blur, leurs lignes se résolvent au blur (event change/input du form).
    form.addEventListener('input', () => { if (hasAttemptedSubmit) scheduleRender() })
    form.addEventListener('change', () => { if (hasAttemptedSubmit) scheduleRender() })

    // Clic sur une ligne du récap → focus le champ + réaffiche sa bulle native
    errorSummaryList?.addEventListener('click', e => {
        const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-error-jump]')
        if (!btn) return
        const field = form.querySelector<Validatable>(`[name="${btn.dataset.errorJump}"]`)
        if (!field) return
        // Groupe de cases (BDD) : on amène le TITRE du groupe à l'écran (« Type de données accédées » / « Technologie
        // d'accès… »), puis on affiche la bulle native — portée par la 1re case, qui reste visible juste sous le titre.
        // (Une bulle native ne peut s'attacher qu'à un champ de formulaire, pas à un <legend>.)
        const legend = field.closest<HTMLElement>('[data-bdd-group]')?.querySelector<HTMLElement>('legend')
        if (legend) {
            legend.scrollIntoView({ block: 'center' })
            field.focus({ preventScroll: true })
        } else {
            field.focus()
        }
        field.reportValidity()
    })

    // Init : filtrer les options + label + visibilité
    const initialProPerso = getProPerso()
    filterTypeEntrepriseOptions(initialProPerso)
    if (selectDemande.value === 'demande_licence_education') selectType.value = initialProPerso === 'particulier' ? 'etudiant' : 'ecole'
    updateTypeLabel(initialProPerso)
    updateVisibility()

    // L'animation d'entrée (data-page-enter) joue une fois au chargement, puis on la neutralise dans le form pour que
    // les blocs révélés conditionnellement (bascule de .hidden) apparaissent instantanément, sans rejouer le fondu.
    // Posé dès la 1re interaction (pointerdown/keydown : AVANT tout changement de visibilité) ou, à défaut, après la
    // durée de l'entrée. Cf. règle CSS scopée #contact-form[data-entered] [data-page-enter].
    const markEntered = (): void => form.setAttribute('data-entered', '')
    form.addEventListener('pointerdown', markEntered, { once: true })
    form.addEventListener('keydown', markEntered, { once: true })
    window.setTimeout(markEntered, 2000)
}
